/**
 * CITY(구매·장착·인벤토리) API — 출금 제외, 내부 동작만.
 */

import { getAPIUrls } from './api';
import { bearerHeaders } from './authHeader';

/**
 * 도시별 설정 목록 (가격·한도)
 * @param {string} [environment]
 * @returns {Promise<{ list: Array<{ id: string, maxSupply: number, priceSpot?: number, priceStar?: number }> }>}
 */
const cityConfigCache = {}; // { [env]: { data, timestamp } }
const CACHE_TTL = 5 * 60 * 1000; // 5분

/** 관리자 저장 직후 등 — 브라우저 메모리 캐시 무효화 */
export function clearCityConfigClientCache() {
  Object.keys(cityConfigCache).forEach((k) => {
    delete cityConfigCache[k];
  });
}

/**
 * 도시별 설정 목록 (가격·한도)
 * Firestore(Functions)가 maxSupply·가격의 소스 오브 트루스, 정적 JSON은 이미지 URL 등 보조 병합.
 * @param {string} [environment]
 * @returns {Promise<{ list: Array<{ id: string, maxSupply: number, mintedCount?: number, priceSpot?: number, priceStar?: number }> }>}
 */
export async function getCityConfig(environment) {
  const envKey = environment || 'default';
  const now = Date.now();

  if (cityConfigCache[envKey] && (now - cityConfigCache[envKey].timestamp < CACHE_TTL)) {
    return cityConfigCache[envKey].data;
  }

  const staticUrl = `/data/city-config-${environment === 'production' ? 'prod' : 'dev'}.json`;
  let staticList = [];
  try {
    const res = await fetch(staticUrl);
    if (res.ok) {
      const data = await res.json();
      staticList = Array.isArray(data.list) ? data.list : [];
    }
  } catch (e) {
    console.warn('[CityApi] Static JSON load failed', e);
  }

  const staticById = Object.fromEntries(staticList.map((s) => [s.id, s]));

  let apiList = null;
  try {
    const urls = getAPIUrls();
    // 전역 발행 수는 city_tokens count()가 정본(게스트·로그인 공통). false 시 캐시만 쓰여 남은 수량이 틀어질 수 있음.
    const payload = { includeMintedCount: true };
    if (environment != null) payload.environment = environment;
    const res = await fetch(urls.getCityConfig, {
      method: 'POST',
      headers: await bearerHeaders(),
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && Array.isArray(data.list) && data.list.length > 0) {
      apiList = data.list;
    }
  } catch (e) {
    console.warn('[CityApi] getCityConfig API failed, using static only', e);
  }

  let list;
  if (apiList) {
    list = apiList.map((item) => {
      const st = staticById[item.id] || {};
      return {
        ...st,
        ...item,
        imageUrl: item.imageUrl || st.imageUrl,
        thumbnailUrl: item.thumbnailUrl || st.thumbnailUrl
      };
    });
  } else {
    list = staticList;
    if (list.length === 0) {
      const urls = getAPIUrls();
      const res = await fetch(urls.getCityConfig, {
        method: 'POST',
        headers: await bearerHeaders(),
        body: JSON.stringify(
          environment != null
            ? { environment, includeMintedCount: true }
            : { includeMintedCount: true }
        )
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to get city config');
      list = data.list || [];
    }
  }

  const result = { list };
  cityConfigCache[envKey] = { data: result, timestamp: now };
  return result;
}

/**
 * 도시별 mintedCount (Static JSON merge용, 1 read 비용 최소화)
 * @param {string} [environment]
 * @returns {Promise<{ counts: Record<string, number> }>}
 */
export async function getCityMintedCounts(environment) {
  const urls = getAPIUrls();
  const res = await fetch(urls.getCityMintedCounts, {
    method: 'POST',
    headers: await bearerHeaders(),
    body: JSON.stringify(environment != null ? { environment } : {})
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { counts: {} };
  return { counts: data.counts || {} };
}

const EMPTY_SLOTS = [null, null, null];

/**
 * 유저 CITY 인벤토리 + 장착 정보 (3슬롯 배열 포함)
 * @param {string} userId
 * @param {string} [environment]
 * @param {{ equippedOnly?: boolean }} [options]
 */
export async function getCityInventory(userId, environment, options = {}) {
  const urls = getAPIUrls();
  const payload = { userId, environment };
  if (options.equippedOnly === true) payload.equippedOnly = true;
  const res = await fetch(urls.getCityInventory, {
    method: 'POST',
    headers: await bearerHeaders(),
    body: JSON.stringify(payload)
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to get inventory');

  const owned = data.owned || [];
  // 🎯 [다중 구매] 도시별 보유 개수 (없으면 owned 배열에서 집계 — 구버전 API 응답 호환)
  const ownedCounts = data.ownedCounts && typeof data.ownedCounts === 'object'
    ? data.ownedCounts
    : owned.reduce((acc, o) => {
        if (o && o.cityId) acc[o.cityId] = (acc[o.cityId] || 0) + 1;
        return acc;
      }, {});
  let equippedTokenIds = Array.isArray(data.equippedTokenIds) ? [...data.equippedTokenIds] : null;
  let equippedList = Array.isArray(data.equippedList) ? [...data.equippedList] : null;

  if (!equippedTokenIds || equippedTokenIds.length !== 3) {
    equippedTokenIds = [...EMPTY_SLOTS];
    if (data.equippedTokenId) equippedTokenIds[0] = data.equippedTokenId;
  }
  if (!equippedList || equippedList.length !== 3) {
    equippedList = [...EMPTY_SLOTS];
    if (data.equipped) equippedList[0] = data.equipped;
  }

  const equipped = equippedList[0] ?? data.equipped ?? null;
  const equippedTokenId = equippedTokenIds[0] ?? data.equippedTokenId ?? null;
  const cityUnlockedSlots =
    typeof data.cityUnlockedSlots === 'number' && data.cityUnlockedSlots >= 1
      ? Math.min(3, data.cityUnlockedSlots)
      : 1;

  return {
    owned,
    equipped,
    equippedTokenId,
    equippedList,
    equippedTokenIds,
    cityUnlockedSlots,
    ownedCounts
  };
}

/**
 * CITY 구매 (SPOT 또는 STAR)
 * @param {string} userId
 * @param {string} cityId
 * @param {'SPOT'|'STAR'} paymentType
 * @param {string} [environment]
 * @returns {Promise<{ success: boolean, tokenId: string, serialNumber: number, spotPoints?: number, ppPoints?: number }>}
 */
export async function purchaseCity(userId, cityId, paymentType, environment) {
  const urls = getAPIUrls();
  const res = await fetch(urls.purchaseCity, {
    method: 'POST',
    headers: await bearerHeaders(),
    body: JSON.stringify({ userId, cityId, paymentType, environment })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.code || 'Purchase failed');
  return data;
}

/**
 * CITY 장착 슬롯 확장 (STAR 차감)
 * @param {string} userId
 * @param {number} slotIndex — 유료 슬롯 인덱스 (1 또는 2, 0은 무료)
 * @param {string} [environment]
 */
export async function purchaseCitySlot(userId, slotIndex, environment) {
  const urls = getAPIUrls();
  const res = await fetch(urls.purchaseCitySlot, {
    method: 'POST',
    headers: await bearerHeaders(),
    body: JSON.stringify({ userId, slotIndex, environment })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.code || 'Slot purchase failed');
  return data;
}

/**
 * CITY 장착
 * @param {string} userId
 * @param {string} tokenId
 * @param {number|string} [slotIndexOrEnvironment] — 슬롯 인덱스(0~2) 또는 구호환: environment만 넘기는 경우
 * @param {string} [environment]
 */
export async function equipCity(userId, tokenId, slotIndexOrEnvironment, environment) {
  let slotIndex = 0;
  let env = environment;
  if (env !== undefined && env !== null) {
    const si = Number(slotIndexOrEnvironment);
    slotIndex = Number.isFinite(si) ? si : 0;
  } else {
    env = slotIndexOrEnvironment;
  }
  const urls = getAPIUrls();
  const res = await fetch(urls.equipCity, {
    method: 'POST',
    headers: await bearerHeaders(),
    body: JSON.stringify({ userId, tokenId, slotIndex, environment: env })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Equip failed');
  return data;
}

/**
 * CITY 장착 해제
 * @param {string} userId
 * @param {number|string} [slotIndexOrEnvironment] — 슬롯 인덱스 또는 구호환: environment만
 * @param {string} [environment]
 */
export async function unequipCity(userId, slotIndexOrEnvironment, environment) {
  let slotIndex = 0;
  let env = environment;
  if (env !== undefined && env !== null) {
    const si = Number(slotIndexOrEnvironment);
    slotIndex = Number.isFinite(si) ? si : 0;
  } else {
    env = slotIndexOrEnvironment;
  }
  const urls = getAPIUrls();
  const res = await fetch(urls.unequipCity, {
    method: 'POST',
    headers: await bearerHeaders(),
    body: JSON.stringify({ userId, slotIndex, environment: env })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Unequip failed');
  return data;
}
